import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getTrustEnabled } from '@/lib/trust';

export type OnboardingState = {
  id: string | null;
  user_id: string;
  step_create_project: boolean;
  step_upload_document: boolean;
  step_start_chat: boolean;
  step_assign_chat_to_project: boolean;
  step_first_ai_message: boolean;
  step_enable_trust: boolean;
  step_enable_os_access: boolean;
  completed: boolean;
  created_at?: string;
};

const DEFAULT_STATE = (userId: string): OnboardingState => ({
  id: null,
  user_id: userId,
  step_create_project: false,
  step_upload_document: false,
  step_start_chat: false,
  step_assign_chat_to_project: false,
  step_first_ai_message: false,
  step_enable_trust: false,
  step_enable_os_access: false,
  completed: false,
  created_at: new Date().toISOString(),
});

export function useOnboardingChecklist() {
  const { user } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [state, setState] = useState<OnboardingState | null>(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setState(null);
      setLoading(false);
      return;
    }
    setLoading(true);

    const { data, error } = await supabase
      .from('onboarding_checklist')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!error && data) {
      setState({
        id: data.id,
        user_id: data.user_id,
        step_create_project: data.step_create_project ?? false,
        step_upload_document: data.step_upload_document ?? false,
        step_start_chat: data.step_start_chat ?? false,
        step_assign_chat_to_project: data.step_assign_chat_to_project ?? false,
        step_first_ai_message: data.step_first_ai_message ?? false,
        step_enable_trust: data.step_enable_trust ?? false,
        step_enable_os_access: data.step_enable_os_access ?? false,
        completed: data.completed ?? false,
        created_at: data.created_at ?? new Date().toISOString(),
      });
      setLoading(false);
      return;
    }

    const defaults = DEFAULT_STATE(user.id);
    const { data: created } = await supabase
      .from('onboarding_checklist')
      .upsert({
        user_id: user.id,
        step_create_project: false,
        step_upload_document: false,
        step_start_chat: false,
        step_assign_chat_to_project: false,
        step_first_ai_message: false,
        step_enable_trust: false,
        step_enable_os_access: false,
        completed: false,
      }, { onConflict: 'user_id' })
      .select('*')
      .single();

    if (created) {
      setState({ ...defaults, id: created.id, created_at: created.created_at });
    } else {
      setState(defaults);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateRemote = useCallback(async (updates: Partial<OnboardingState>) => {
    if (!user) return false;
    const recordId = state?.id;

    const merged = { ...(state || DEFAULT_STATE(user.id)), ...updates };
    const allDone = merged.step_create_project &&
      merged.step_upload_document &&
      merged.step_start_chat &&
      merged.step_assign_chat_to_project &&
      merged.step_first_ai_message &&
      merged.step_enable_trust &&
      merged.step_enable_os_access;

    if (!recordId) {
      const { data, error } = await supabase
        .from('onboarding_checklist')
        .upsert({
          user_id: user.id,
          step_create_project: merged.step_create_project,
          step_upload_document: merged.step_upload_document,
          step_start_chat: merged.step_start_chat,
          step_assign_chat_to_project: merged.step_assign_chat_to_project,
          step_first_ai_message: merged.step_first_ai_message,
          step_enable_trust: merged.step_enable_trust,
          step_enable_os_access: merged.step_enable_os_access,
          completed: allDone,
        }, { onConflict: 'user_id' })
        .select('*')
        .single();

      if (error) {
        return false;
      }
      if (data) {
        setState({
          id: data.id,
          user_id: data.user_id,
          step_create_project: data.step_create_project,
          step_upload_document: data.step_upload_document,
          step_start_chat: data.step_start_chat,
          step_assign_chat_to_project: data.step_assign_chat_to_project,
          step_first_ai_message: data.step_first_ai_message,
          step_enable_trust: data.step_enable_trust,
          step_enable_os_access: data.step_enable_os_access,
          completed: data.completed,
        });
      } else {
        // Optimistic if no row returned
        setState({ ...merged, completed: allDone });
      }
      // Always re-fetch canonical state
      await refresh();
      return true;
    }

    const { data, error } = await supabase
      .from('onboarding_checklist')
      .update({
        step_create_project: merged.step_create_project,
        step_upload_document: merged.step_upload_document,
        step_start_chat: merged.step_start_chat,
        step_assign_chat_to_project: merged.step_assign_chat_to_project,
        step_first_ai_message: merged.step_first_ai_message,
        step_enable_trust: merged.step_enable_trust,
        step_enable_os_access: merged.step_enable_os_access,
        completed: allDone,
      })
      .eq('id', recordId)
      .select('*')
      .single();

    if (error) {
      return false;
    }
    if (data) {
      setState({
        id: data.id,
        user_id: data.user_id,
        step_create_project: data.step_create_project,
        step_upload_document: data.step_upload_document,
        step_start_chat: data.step_start_chat,
        step_assign_chat_to_project: data.step_assign_chat_to_project,
        step_first_ai_message: data.step_first_ai_message,
        step_enable_trust: data.step_enable_trust,
        step_enable_os_access: data.step_enable_os_access,
        completed: data.completed,
      });
    } else {
      // Optimistic if no row returned
      setState({ ...merged, completed: allDone });
    }
    // Always re-fetch canonical state
    await refresh();
    return true;
  }, [state, user, refresh]);

  const setStep = useCallback(async (key: keyof OnboardingState, value: boolean) => {
    if (!state) return false;
    if (!key.startsWith('step_')) return false;
    return updateRemote({ [key]: value } as Partial<OnboardingState>);
  }, [state, updateRemote]);

  const autoDetect = useCallback(async () => {
    if (!user) {
      // Safe fallback: no user -> no detection
      return { ok: false, detected: {} as Record<string, boolean> };
    }
    // Only consider actions after the checklist was created to avoid false positives
    const thresholdISO = state?.created_at;
    if (!thresholdISO) {
      // If we lack created_at, return a harmless result so UI doesn't show "unavailable"
      return { ok: false, detected: {} as Record<string, boolean> };
    }

    const { data: projects } = await supabase
      .from('projects')
      .select('id, created_at')
      .eq('user_id', user.id)
      .gte('created_at', thresholdISO)
      .limit(1);

    const { data: kbs } = await supabase
      .from('knowledge_bases')
      .select('id')
      .eq('created_by', user.id);

    let hasKbDoc = false;
    if (kbs && kbs.length > 0) {
      const kbIds = kbs.map(k => k.id);
      const { data: docs } = await supabase
        .from('knowledge_base_documents')
        .select('id, created_at')
        .in('knowledge_base_id', kbIds)
        .gte('created_at', thresholdISO)
        .limit(1);
      hasKbDoc = (docs || []).length > 0;
    }

    const { data: chats } = await supabase
      .from('chats')
      .select('id, project_id, created_at')
      .eq('user_id', user.id)
      .gte('created_at', thresholdISO)
      .limit(50);

    const hasAssignedChat = (chats || []).some(c => !!c.project_id);

    let hasMessage = false;
    if (chats && chats.length > 0) {
      const chatIds = chats.map(c => c.id);
      const { data: msgs } = await supabase
        .from('messages')
        .select('id, timestamp')
        .in('chat_id', chatIds)
        .gte('timestamp', thresholdISO)
        .limit(1);
      hasMessage = (msgs || []).length > 0;
    }

    const trustEnabled = getTrustEnabled();

    const { data: osProj } = await supabase
      .from('projects')
      .select('id, updated_at')
      .eq('user_id', user.id)
      .eq('os_access_enabled', true)
      .gte('updated_at', thresholdISO)
      .limit(1);

    const detected = {
      step_create_project: (projects || []).length > 0,
      step_upload_document: hasKbDoc,
      step_start_chat: (chats || []).length > 0,
      step_assign_chat_to_project: hasAssignedChat,
      step_first_ai_message: hasMessage,
      step_enable_trust: trustEnabled,
      step_enable_os_access: (osProj || []).length > 0,
    };

    // Only promote steps to true (never force false)
    const updates: Partial<OnboardingState> = {};
    const updatedKeys: string[] = [];
    if (detected.step_create_project && !state?.step_create_project) { updates.step_create_project = true; updatedKeys.push('step_create_project'); }
    if (detected.step_upload_document && !state?.step_upload_document) { updates.step_upload_document = true; updatedKeys.push('step_upload_document'); }
    if (detected.step_start_chat && !state?.step_start_chat) { updates.step_start_chat = true; updatedKeys.push('step_start_chat'); }
    if (detected.step_assign_chat_to_project && !state?.step_assign_chat_to_project) { updates.step_assign_chat_to_project = true; updatedKeys.push('step_assign_chat_to_project'); }
    if (detected.step_first_ai_message && !state?.step_first_ai_message) { updates.step_first_ai_message = true; updatedKeys.push('step_first_ai_message'); }
    if (detected.step_enable_trust && !state?.step_enable_trust) { updates.step_enable_trust = true; updatedKeys.push('step_enable_trust'); }
    if (detected.step_enable_os_access && !state?.step_enable_os_access) { updates.step_enable_os_access = true; updatedKeys.push('step_enable_os_access'); }

    if (updatedKeys.length === 0) {
      return { ok: true, detected, updatedKeys };
    }

    const ok = await updateRemote(updates);
    return { ok, detected, updatedKeys };
  }, [user, updateRemote, state?.created_at, state?.step_create_project, state?.step_upload_document, state?.step_start_chat, state?.step_assign_chat_to_project, state?.step_first_ai_message, state?.step_enable_trust, state?.step_enable_os_access]);

  const progress = useMemo(() => {
    if (!state) return { count: 0, total: 7 };
    const list = [
      state.step_create_project,
      state.step_upload_document,
      state.step_start_chat,
      state.step_assign_chat_to_project,
      state.step_first_ai_message,
      state.step_enable_trust,
      state.step_enable_os_access,
    ];
    return { count: list.filter(Boolean).length, total: list.length };
  }, [state]);

  return {
    loading,
    state,
    progress,
    setStep,
    refresh,
    autoDetect,
  };
}